import { Fragment, useMemo, useState } from "react";
import { Loading, SummaryCard, showSuccess, showApiError } from "@common";
import { formatMoney, canManageSchedulePayments, canDeleteSchedulePayments } from "@utils";
import { useAuth } from "@api/useAuth";
import {
  useBankAccounts,
  useDailyBankBalances,
  useSchedulePayments,
  useStatementPayments,
  useCreateSchedulePaymentException,
  useUpdateSchedulePaymentException,
  useBulkSchedulePaymentExceptions,
  useUpdateSchedulePayment,
  useDeleteSchedulePayment,
  useUpdateStatementPayment,
} from "@api/hooks";
import MoveOccurrenceModal from "./MoveOccurrenceModal";
import SchedulePaymentModal from "./SchedulePaymentModal";
import useNonBankingMovePrompt from "./useNonBankingMovePrompt";
import ConfirmNonBankingMovesModal from "./ConfirmNonBankingMovesModal";
import CurrencyBadge from "./CurrencyBadge";
import {
  CARD_PAYMENT_KEY,
  CARD_PAYMENT_LABEL,
  buildScheduleOutflowsByAccount,
  sortOutflowRows,
  OTHER_ROW_KEY,
} from "./scheduleOccurrences";
import {
  computeBalanceSeries,
  fmtClockTime,
  getDayWindow,
  groupDaysIntoWeeks,
  shiftISO,
} from "./ClinicCashFlowCard";

function Chevron({ open, className = "w-3 h-3" }) {
  return (
    <svg
      className={`${className} shrink-0 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
      viewBox="0 0 10 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Card / LOC balances are money OWED, bank balances are cash HELD. They must not
// be added together, so the summary totals them separately.
const LIABILITY_TYPES = new Set(["credit_card", "line_of_credit"]);

// Display order and header colour per account kind: RBC -> LOC -> AMEX -> Visa.
// Cards are split by label because `account_type` is `credit_card` for both
// networks. Anything else (incl. the combined card row) falls in with Visa.
// Class strings are literal so Tailwind's JIT picks them up.
const ACCOUNT_KINDS = {
  bank: {
    rank: 0,
    row: "bg-palette-blue-soft",
    edge: "border-l-4 border-palette-blue-badge",
    text: "text-palette-blue-text",
  },
  loc: {
    rank: 1,
    row: "bg-palette-amber-soft",
    edge: "border-l-4 border-palette-amber-badge",
    text: "text-palette-amber-text",
  },
  amex: {
    rank: 2,
    row: "bg-palette-teal-soft",
    edge: "border-l-4 border-palette-teal-badge",
    text: "text-palette-teal-text",
  },
  visa: {
    rank: 3,
    row: "bg-palette-violet-soft",
    edge: "border-l-4 border-palette-violet-badge",
    text: "text-palette-violet-text",
  },
};

function accountKind(account) {
  if (account.account_type === "bank") return ACCOUNT_KINDS.bank;
  if (account.account_type === "line_of_credit") return ACCOUNT_KINDS.loc;
  if (/^amex\b/i.test(account.label ?? "")) return ACCOUNT_KINDS.amex;
  return ACCOUNT_KINDS.visa;
}

function compareAccounts(a, b) {
  return (
    accountKind(a).rank - accountKind(b).rank ||
    (a.label ?? "").localeCompare(b.label ?? "", undefined, { numeric: true })
  );
}

// Stable default for the controlled `collapsedAccounts` prop — a fresh `new Set()`
// in the parameter list would be a new identity every render.
const EMPTY_SET = new Set();

// Line items per account, mirroring ClinicCashFlowCard's row layout so the two
// pages read the same way: balances top and bottom, flows between.
//
// Both flow rows are SECTIONS: each carries a total and expands into the
// individual sources behind it. `dir` drives sign, colour and which series the
// cell reads, so one code path serves money in and money out.
const ROW_DEFS = [
  { key: "opening", label: "Opening Balance", type: "balance" },
  // Reference only — the manually entered end-of-day figure. `type: "reference"`
  // keeps it out of every balance/flow/net code path, and the row is dropped for
  // accounts with no closing data (see `lineItems`).
  { key: "closing", label: "Closing Balance", sublabel: "for reference", type: "reference" },
  { key: "inflows", label: "Inflows", type: "section", dir: "in" },
  { key: "outflows", label: "Outflows", type: "section", dir: "out" },
  { key: "net", label: "Net Cashflow", type: "net" },
  { key: "ending", label: "Ending Balance", type: "balance" },
];

// The same statement seen from the receiving side. A separate key from the one
// above so an account that somehow appeared on both sides could never have its
// two directions collapse into one series.
const CARD_PAYMENT_IN_KEY = "__card_payment_in";

// Stable id for the combined credit-card row in "By Visa" mode. Namespaced so it
// can never collide with a real account id, which is what the collapse set holds.
const CARD_AGGREGATE_ID = "__all_credit_cards";

function fmtOrDash(v) {
  return v == null || Number.isNaN(v) ? "—" : formatMoney(v);
}

function fmtDayHeader(iso) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
  });
}

/** Whole days from `a` to `b` (both ISO). Negative when `b` precedes `a`. */
function diffDays(a, b) {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round((new Date(by, bm - 1, bd) - new Date(ay, am - 1, ad)) / 86400000);
}

// How far back the balance projection will reach for its starting point. An
// account whose last entered balance is older than this projects from that cap
// instead, so one stale account cannot make the grid generate years of days.
const MAX_PROJECTION_LOOKBACK_DAYS = 400;

/**
 * Entity cash-flow grid: one collapsible block per BANK ACCOUNT of the selected
 * entities, each showing the standard cash-flow line items down the side and days
 * across the top — the same shape as the clinic cards, at account granularity.
 *
 * WHAT IS REAL vs PENDING:
 *   Opening / Ending Balance PROJECT forward through the flows, via the same
 *     `computeBalanceSeries` the clinic cards use — reused, not reimplemented,
 *     so the two pages cannot drift on how a balance rolls:
 *       opening(day) = the entered balance if there is one, else the previous
 *                      day's ending (an estimate that resets at any real entry)
 *       ending(day)  = opening + (inflows − outflows)
 *     Only the opening is marked as real/estimate; ending is always derived.
 *     On a CREDIT CARD the entered figure is what is OWED and these rows show
 *     exactly that — the CREDIT BALANCE, relabelled "Credit Balance" and drawn
 *     in red. The flows are swapped into the projection for it (a statement
 *     payment received pays the card DOWN), and no `credit_limit` is needed, so
 *     a card without one still projects. On an LOC the entry is what is DRAWN
 *     and those rows still show `credit_limit − drawn`, relabelled "Available".
 *     A bank account's entry is already cash held and passes through untouched.
 *     The summary cards use the RAW owed figure throughout, since Credit Owed /
 *     Credit Available report both sides.
 *   Outflows are real, from two sources summed into one section:
 *     - active SchedulePayments (keyed on `bank_account`), with occurrences
 *       computed client-side by `scheduleOccurrences.js` — the same generator the
 *       clinic cards use — broken out per expense type;
 *     - the FROM side of an active StatementPayment, as a "Credit card payment"
 *       child row.
 *   Inflows are the TO side of a StatementPayment: paying a card down is money
 *     arriving at that card account. One split therefore lands twice — an outflow
 *     on the paying bank account and an inflow on the card — which is correct,
 *     they are two different accounts. It expands like Outflows, into a single
 *     "Statement payment received" child however many banks paid — the payers are
 *     named in the hover rather than given a row each, which would multiply rows
 *     for no extra structure.
 *   Net Cashflow is inflows − outflows.
 *
 * Hovering any flow cell lists what made it up — just each payment's source and
 * amount — gathered across every day the column covers, so a weekly or
 * collapsed-Past total explains itself instead of being an unexplained lump.
 *
 * THE PROJECTION IS ONLY AS COMPLETE AS THE FLOWS BEHIND IT. Operating inflows
 * (forecasts, collections, cash transactions) all key on clinic, and entity
 * accounts have none — so an entity BANK account rolls forward with its payments
 * deducted and none of the revenue that funds them, and its Ending Balance
 * drifts low the further out you look. Any real entered balance resets the drift
 * on the day it lands. A CARD is complete by comparison: statements are the only
 * modelled movement, so its projection is right until someone charges the card.
 *
 * Past (before-today) columns collapse into one, as on the clinic cards. A FLOW
 * sums across every day the column covers; a BALANCE never does — the collapsed
 * column takes its OPENING from the first day of the range and its ENDING from
 * the last, since summing a position is meaningless.
 */
export default function EntityCashFlowTable({
  entityIds = [],
  // Bank accounts to show. `null` means "no filter" so the component still works
  // standalone; an explicit [] really does mean nothing selected.
  accountIds = null,
  entityNameById = {},
  days,
  // "daily" (one column per day) or "weekly" (Monday-anchored buckets), the
  // same two modes the clinic cards offer.
  viewMode = "daily",
  // Ids of accounts whose line items are hidden. Controlled by the page so its
  // Expand/Collapse All button and these rows' chevrons share one source.
  collapsedAccounts = EMPTY_SET,
  onCollapsedAccountsChange = null,
  // Whether payment dates can be moved from a cell. Mirrors the clinic grid,
  // where Bookkeeper reaches the tab read-only.
  canMoveOccurrence: canMoveProp,
  // "account" → one block per account (default). "card" → every credit card
  // combined into a single block, the entity-page sibling of ClinicCashFlow's
  // "By Company".
  groupMode = "account",
}) {
  const { user } = useAuth();
  const canMove = canMoveProp ?? canManageSchedulePayments(user);

  // Filter by entity_id, not name — names contain apostrophes and matching on
  // them is fragile. Joined as CSV; the backend accepts repeated params too.
  const entityParam = entityIds.join(",");
  // `has_clinic=false` — entity-level accounts only. An account with both a clinic
  // and an entity (the SFC pseudo-clinic's RBC S.F. Corporation - [9411]) belongs
  // to the clinic-scoped cash flow, and would otherwise appear here too. Keep this
  // filter identical to DensNDenteCashFlow's so both share one react-query entry.
  const accountsQuery = useBankAccounts(
    { status: "active", entity_id: entityParam, has_clinic: "false", limit: 500 },
    { enabled: entityIds.length > 0 },
  );
  // date_to bounds the fetch to the window's end; earlier entries are needed so a
  // window that opens before the first entry can still carry a balance forward.
  const to = days.length ? days[days.length - 1].date : undefined;
  const balancesQuery = useDailyBankBalances(
    { entity_id: entityParam, date_to: to, limit: 1000 },
    { enabled: entityIds.length > 0 && !!to },
  );

  // The server scopes by entity; the account picker narrows further, client-side,
  // because it selects from the very list this query returned.
  const accounts = useMemo(() => {
    const all = accountsQuery.data?.results ?? accountsQuery.data ?? [];
    const keep = Array.isArray(accountIds) ? new Set(accountIds) : null;
    return all.filter((a) => !keep || keep.has(a.id)).sort(compareAccounts);
  }, [accountsQuery.data, accountIds]);

  // Scheduled payments for exactly the accounts on screen. Scoped by account id
  // rather than entity — the endpoint has no entity filter, and a payment is
  // tied to an account anyway. Held until the accounts resolve, since an
  // unfiltered request would pull every clinic's schedules.
  const accountIdParam = accounts.map((a) => a.id).join(",");
  // No `status` filter, matching ClinicCashFlow: a cancelled schedule's
  // pre-`end_date` occurrences are real payments and stay on the grid.
  const schedulesQuery = useSchedulePayments(
    { bank_account: accountIdParam, limit: 1000 },
    { enabled: accounts.length > 0 },
  );

  // Credit-card statements. Scoped by entity (the endpoint filters on the CARD's
  // entity) rather than by the visible accounts, because a statement touches two
  // accounts and either side may be filtered out of view while the other is not.
  const statementsQuery = useStatementPayments(
    { status: "active", entity_id: entityParam, limit: 500 },
    { enabled: entityIds.length > 0 },
  );

  const from = days.length ? days[0].date : null;

  // ── where the projection has to START ──────────────────────────────────────
  // NOT the visible window. `opening(day) = ending(day − 1)` has to hold across
  // window boundaries too, so paging from "Sep 2 – Oct 1" to "Oct 2 – Oct 31"
  // opens on the Oct 1 ending you were just looking at.
  //
  // Seeding with the last entered balance alone does not achieve that: every
  // payment between that entry and the window start would be skipped, and the
  // two windows would disagree. So the series is computed from the last entered
  // balance FORWARD through all the flows in between, and only the visible slice
  // is rendered.
  //
  // Capped: a stale account whose last entry is years old would otherwise
  // generate an unbounded day list.
  const seriesFrom = useMemo(() => {
    if (!from) return null;
    const entries = balancesQuery.data?.results ?? balancesQuery.data ?? [];
    const visible = new Set(accounts.map((a) => a.id));
    // The LAST entry before the window per account, then the EARLIEST of those —
    // one shared day array has to reach back far enough for every account.
    const lastByAccount = {};
    entries.forEach((e) => {
      if (!visible.has(e.bank_account) || e.date >= from) return;
      if (!lastByAccount[e.bank_account] || e.date > lastByAccount[e.bank_account]) {
        lastByAccount[e.bank_account] = e.date;
      }
    });
    const earliest = Object.values(lastByAccount).sort()[0];
    if (!earliest) return from;
    const floor = shiftISO(from, -MAX_PROJECTION_LOOKBACK_DAYS);
    return earliest < floor ? floor : earliest;
  }, [balancesQuery.data, accounts, from]);

  // Days the PROJECTION spans: [seriesFrom … to]. A superset of the visible
  // `days`, which is what makes the roll-forward continuous.
  const seriesDays = useMemo(() => {
    if (!seriesFrom || !to) return days;
    return getDayWindow(seriesFrom, diffDays(seriesFrom, to) + 1);
  }, [seriesFrom, to, days]);

  // Flows are generated over the PROJECTION range, not the visible one, so the
  // roll-forward sees the payments between the last entered balance and the
  // window. Cells only ever read the visible dates, so the extra keys are inert
  // there.
  const { valuesByAccount, extraRowsByAccount, occurrencesByAccount } = useMemo(() => {
    if (!seriesFrom || !to) {
      return { valuesByAccount: {}, extraRowsByAccount: {}, occurrencesByAccount: {} };
    }
    const rowsData = schedulesQuery.data?.results ?? schedulesQuery.data ?? [];
    return buildScheduleOutflowsByAccount(rowsData, seriesFrom, to);
  }, [schedulesQuery.data, seriesFrom, to]);

  // Same rule as the clinic grid: an occurrence landing on a weekend or a bank
  // holiday is offered forward to the next banking day as a one-off exception,
  // so the projection below rolls a balance on dates money can actually move.
  const nonBankingMoves = useNonBankingMovePrompt(occurrencesByAccount, { enabled: canMove });

  // A statement payment moves money BOTH ways, so one split lands on two rows:
  //   the FROM bank account  -> outflow  (cash leaves)
  //   the TO card account    -> inflow   (the balance owed is paid down)
  // Amounts come straight from `splits`, each already carrying its own date, so
  // a split falls in the column for the day it is actually paid.
  const statementFlows = useMemo(() => {
    const rowsData = statementsQuery.data?.results ?? statementsQuery.data ?? [];
    const byAccount = {};
    const bucket = (id) =>
      (byAccount[id] = byAccount[id] ?? {
        statementIn: {},
        statementOut: {},
        // { [rowKey]: { [date]: [{label, amount}] } } — what a hovered cell lists.
        details: {},
      });

    // Deliberately just a name and an amount for DISPLAY. The statement's own
    // terms (due date, plan, original balance) belong on the Monthly Inputs tab,
    // not in a tooltip you are skimming to answer "what is this number".
    //
    // `move` is the identity needed to relocate the payment when a cell is
    // clicked. It is not rendered.
    const addDetail = (acct, rowKey, date, label, amount, move) => {
      const perRow = (acct.details[rowKey] = acct.details[rowKey] ?? {});
      (perRow[date] = perRow[date] ?? []).push({ label, amount, move });
    };

    rowsData.forEach((s) => {
      const cardLabel = s.to_card_account_label ?? "Credit card";
      const bankLabel = s.from_bank_account_label ?? "Bank account";

      (Array.isArray(s.splits) ? s.splits : []).forEach((split, splitIndex) => {
        const date = split?.date;
        const amount = Number(split?.amount);
        // Bounded by the PROJECTION range, not the visible one — splits before
        // the window still have to move the balance that rolls into it.
        if (
          !date ||
          !Number.isFinite(amount) ||
          (seriesFrom && date < seriesFrom) ||
          (to && date > to)
        ) {
          return;
        }

        // A statement has NO exception model — moving one of its payments edits
        // the `splits` array on the statement itself. Identified by INDEX, not
        // by date: a statement may legitimately pay twice on one day, and
        // matching on date would move whichever came first.
        const move = {
          kind: "statement",
          key: `stm-${s.id}-${splitIndex}`,
          statementId: s.id,
          splitIndex,
          currentDate: date,
        };

        if (s.from_bank_account != null) {
          const acct = bucket(s.from_bank_account);
          acct.statementOut[date] = (acct.statementOut[date] ?? 0) + amount;
          addDetail(acct, CARD_PAYMENT_KEY, date, cardLabel, amount, move);
        }

        if (s.to_card_account != null) {
          const acct = bucket(s.to_card_account);
          acct.statementIn[date] = (acct.statementIn[date] ?? 0) + amount;
          // One child row for card payments regardless of how many banks paid —
          // the payers are named in the hover, not given a row each.
          addDetail(acct, CARD_PAYMENT_IN_KEY, date, bankLabel, amount, move);
        }
      });
    });
    return byAccount;
  }, [statementsQuery.data, seriesFrom, to]);

  const rows = useMemo(() => {
    const entries = balancesQuery.data?.results ?? balancesQuery.data ?? [];
    const byAccount = new Map();
    entries.forEach((e) => {
      if (!byAccount.has(e.bank_account)) byAccount.set(e.bank_account, []);
      byAccount.get(e.bank_account).push(e);
    });

    return accounts.map((account) => {
      const own = (byAccount.get(account.id) ?? [])
        .slice()
        .sort((a, b) => (a.date < b.date ? -1 : 1));

      const isLiability = LIABILITY_TYPES.has(account.account_type);
      // A CREDIT CARD reports what it OWES, not its headroom — see `toDisplay`.
      // A line of credit is still shown as available credit, so the two liability
      // types diverge here and `isLiability` alone no longer decides the scale.
      const isCard = account.account_type === "credit_card";
      const creditLimit = account.credit_limit != null ? Number(account.credit_limit) : null;

      // What a balance row SHOWS depends on the account type:
      //   bank        -> the entered figure as-is; it is cash held.
      //   credit card -> the entered figure as-is too: it is the CREDIT BALANCE,
      //                  what the card owes. Requested over headroom because it
      //                  is the figure that reconciles against the statement.
      //                  It also needs no `credit_limit`, so a card without one
      //                  now projects instead of showing a column of dashes.
      //   LOC         -> limit MINUS the entered figure. The entry is the amount
      //                  drawn, and a drawn figure sitting in the same column as
      //                  a bank's cash would read as money available when it is
      //                  the opposite, so this one still shows headroom.
      // Null limit yields null rather than `0 - drawn`, which would render a
      // negative number as if it were a real position.
      const toDisplay = (owed) => {
        if (owed == null) return null;
        if (!isLiability || isCard) return owed;
        if (creditLimit == null) return null;
        return creditLimit - owed;
      };

      // Flows first — the balance projection consumes them.
      //
      // Two outflow sources land on the same rows: scheduled payments (broken
      // down per expense type) and card statements. They are summed into the
      // section total, and the statement side also gets its own child row so a
      // card payment is not indistinguishable from a rent cheque.
      const scheduled = valuesByAccount[account.id] ?? {};
      const statement = statementFlows[account.id] ?? {};
      const cardOut = statement.statementOut ?? {};
      const inflows = statement.statementIn ?? {};
      const hasCardOut = Object.keys(cardOut).length > 0;

      const outflows = { ...(scheduled.outflows ?? {}) };
      Object.entries(cardOut).forEach(([date, amt]) => {
        outflows[date] = (outflows[date] ?? 0) + amt;
      });

      // Sorted AFTER the card-payment child is appended, so "Other payments" ends
      // up last overall rather than last-among-schedules.
      const outflowChildren = sortOutflowRows([
        ...(extraRowsByAccount[account.id] ?? []),
        ...(hasCardOut ? [{ key: CARD_PAYMENT_KEY, label: CARD_PAYMENT_LABEL }] : []),
      ]);

      // Inflows mirrors Outflows: expandable, with one child however many banks
      // paid. Named for the receiving side — "Credit card payment" is the
      // outflow row on the paying bank, and reusing it here would make the two
      // directions of the same statement read identically.
      const inflowChildren = Object.keys(inflows).length
        ? [{ key: CARD_PAYMENT_IN_KEY, label: "Statement payment received" }]
        : [];

      // Per-cell hover detail. Scheduled occurrences already carry a label and
      // an amount, so they are reused rather than re-derived; statement details
      // were collected above. The section rows concatenate their children, which
      // is what makes hovering the Outflows total list everything inside it.
      const occ = occurrencesByAccount[account.id] ?? {};
      const details = { ...(statement.details ?? {}) };
      Object.entries(occ).forEach(([rowKey, byDay]) => {
        const perRow = (details[rowKey] = details[rowKey] ?? {});
        Object.entries(byDay).forEach(([date, items]) => {
          perRow[date] = [
            ...(perRow[date] ?? []),
            ...items.map((it) => ({
              label: it.label,
              amount: it.amount,
              // Scheduled payments DO have an exception model — moving one
              // creates or updates a one-off exception, leaving the recurring
              // schedule untouched.
              move: {
                kind: "schedule",
                key: `sch-${it.scheduleId}-${it.originalDate}`,
                scheduleId: it.scheduleId,
                originalDate: it.originalDate,
                exceptionId: it.exceptionId,
                currentDate: it.currentDate ?? date,
              },
            })),
          ];
        });
      });
      // "Other payments" expands into one row per "Specify expense type" text.
      // Grouped on the trimmed, case-folded text so "Parking" and "parking "
      // share a row; the first spelling seen names it. Built off the occurrences
      // already in `details`, so each sub-row sums, itemises and moves exactly
      // like the child it came from.
      const otherFlows = {};
      const otherLabels = new Map();
      Object.entries(details[OTHER_ROW_KEY] ?? {}).forEach(([date, items]) => {
        items.forEach((it) => {
          const text =
            String(it.label ?? "")
              .trim()
              .replace(/\s+/g, " ") || "Unspecified";
          const key = `${OTHER_ROW_KEY}:${text.toLowerCase()}`;
          if (!otherLabels.has(key)) otherLabels.set(key, text);
          const flow = (otherFlows[key] = otherFlows[key] ?? {});
          flow[date] = (flow[date] ?? 0) + (Number(it.amount) || 0);
          const perRow = (details[key] = details[key] ?? {});
          perRow[date] = [...(perRow[date] ?? []), it];
        });
      });
      const otherChildren = [...otherLabels]
        .map(([key, label]) => ({ key, label }))
        .sort((a, b) => a.label.localeCompare(b.label));

      const mergeInto = (target, keys) => {
        keys.forEach((k) => {
          Object.entries(details[k] ?? {}).forEach(([date, items]) => {
            target[date] = [...(target[date] ?? []), ...items];
          });
        });
      };
      // Each section's own details are the union of its children's, so hovering
      // a section total lists everything inside it.
      details.outflows = {};
      mergeInto(
        details.outflows,
        outflowChildren.map((c) => c.key),
      );
      details.inflows = {};
      mergeInto(
        details.inflows,
        inflowChildren.map((c) => c.key),
      );

      // Entered balances, converted to display space BEFORE projecting, so the
      // roll-forward happens on the same scale the rows show.
      const realBalanceByDay = {};
      own.forEach((e) => {
        realBalanceByDay[e.date] = toDisplay(Number(e.balance));
      });

      // Closing balance — reference only. Deliberately NOT run through
      // `toDisplay`: it is the figure someone typed on the Daily Bank Balance
      // page and is shown back exactly as entered. On a bank and now on a CARD
      // that is already the same scale as the rows above; only an LOC's rows sit
      // in headroom space while this stays as drawn. It feeds no projection, so
      // there is nothing for the conversion to keep consistent. A malformed blob
      // is skipped rather than shown as zero.
      const closingByDate = {};
      own.forEach((e) => {
        const c = e.closing_balance;
        if (c && typeof c === "object") {
          const amount = parseFloat(c.balance);
          if (!Number.isNaN(amount)) closingByDate[e.date] = { amount, time: c.time ?? null };
        }
      });
      // Seed: the latest entry strictly before the PROJECTION start (not the
      // visible window) — everything from there on is rolled forward below.
      let seedRaw = null;
      own.forEach((e) => {
        if (seriesFrom && e.date < seriesFrom) seedRaw = Number(e.balance);
      });

      // Same projection the clinic cards use — reused rather than reimplemented
      // so the two pages cannot drift on how a balance rolls forward:
      //   opening(day) = the entered balance if there is one, else the previous
      //                  day's ending (an estimate that resets at any real entry)
      //   ending(day)  = opening + (inflows − outflows)
      //
      // Run over `seriesDays`, which reaches back to the last entered balance,
      // so the first VISIBLE day opens on the ending the previous window closed
      // on rather than on a stale entry.
      //
      // The formula holds for an LOC too, in available-credit space: paying it
      // down is an inflow here and raises the headroom by exactly that amount.
      //
      // A CREDIT CARD is in OWED space, where the flows push the OTHER way: a
      // statement payment received pays the card DOWN, and a charge scheduled
      // against the card adds to what it owes. Swapping the two arguments keeps
      // `computeBalanceSeries` untouched and shared with the clinic cards rather
      // than teaching it about signs. The ROWS are not swapped — Inflows still
      // reads as money arriving at the card.
      const series = computeBalanceSeries({
        days: seriesDays,
        realBalanceByDay,
        seedBalance: toDisplay(seedRaw),
        inflows: isCard ? outflows : inflows,
        outflows: isCard ? inflows : outflows,
      });

      const byDate = {};
      days.forEach((d) => {
        byDate[d.date] = {
          opening: series.opening[d.date] ?? null,
          ending: series.ending[d.date] ?? null,
          openingIsReal: !!series.openingIsReal[d.date],
          closing: closingByDate[d.date] ?? null,
        };
      });
      // Whether this account has ANY closing figure in the visible window —
      // decides if the reference row is worth a line at all (see `lineItems`).
      const hasClosing = Object.values(byDate).some((s) => s.closing);

      const latest = own.length ? own[own.length - 1] : null;

      return {
        account,
        byDate,
        hasClosing,
        flows: {
          ...scheduled,
          ...otherFlows,
          outflows,
          [CARD_PAYMENT_KEY]: cardOut,
          inflows,
          [CARD_PAYMENT_IN_KEY]: inflows,
        },
        details,
        childrenBySection: { inflows: inflowChildren, outflows: outflowChildren },
        otherChildren,
        // RAW owed / held, deliberately unconverted — the summary cards report
        // credit owed against the limit, which needs the entered figure.
        latest: latest ? Number(latest.balance) : null,
        isLiability,
        isCard,
        entityName: account.entity_name ?? entityNameById[account.entity] ?? "—",
      };
    });
  }, [
    accounts,
    balancesQuery.data,
    days,
    seriesDays,
    seriesFrom,
    entityNameById,
    valuesByAccount,
    extraRowsByAccount,
    occurrencesByAccount,
    statementFlows,
  ]);

  // "By Visa" view: every CREDIT CARD folded into one row. The sibling of
  // ClinicCashFlow.jsx's `companyAggregate`, which does the same for clinics.
  //
  // Cards only, deliberately. A bank holds cash and a card owes it; the balance
  // rows here carry the CREDIT BALANCE owed for a card and cash held for a bank,
  // so summing the two would net a debt against a cash position and call the
  // result a number. Restricting to one account type keeps every summed row on
  // one scale — and since that scale is now owed rather than headroom, the sum no
  // longer depends on every card having a `credit_limit` on file.
  //
  // Flows sum straight by date. Opening/Ending are summed from the already-
  // projected per-card series rather than re-projected off combined flows: the
  // real-vs-estimate rule is per-account, and an opening counts as real for the
  // group only when every contributing card's opening that day is real — the same
  // rule the clinic aggregate uses.
  const cardAggregate = useMemo(() => {
    if (groupMode !== "card") return null;
    const cards = rows.filter((r) => r.account.account_type === "credit_card");
    if (!cards.length) return null;

    const flows = {};
    const details = {};
    const childKeys = { inflows: new Map(), outflows: new Map() };
    const otherKeys = new Map();
    const opening = {};
    const ending = {};
    const realCount = {};
    const openCount = {};
    let owed = 0;
    let limit = 0;
    const currencies = new Set();

    cards.forEach((r) => {
      Object.entries(r.flows).forEach(([key, byDate]) => {
        if (!byDate || typeof byDate !== "object") return;
        const target = (flows[key] = flows[key] || {});
        Object.entries(byDate).forEach(([date, amt]) => {
          target[date] = (target[date] || 0) + (amt || 0);
        });
      });

      // Hover detail is concatenated, not summed — the point of it is naming the
      // individual payments, and which card each came from is already in its label.
      Object.entries(r.details).forEach(([key, byDate]) => {
        const target = (details[key] = details[key] || {});
        Object.entries(byDate).forEach(([date, items]) => {
          target[date] = [...(target[date] ?? []), ...items];
        });
      });

      // Union of child rows: a card with no scheduled rent should not erase the
      // row for one that has it.
      ["inflows", "outflows"].forEach((section) => {
        (r.childrenBySection[section] ?? []).forEach((c) => childKeys[section].set(c.key, c.label));
      });
      (r.otherChildren ?? []).forEach((c) => {
        if (!otherKeys.has(c.key)) otherKeys.set(c.key, c.label);
      });

      days.forEach(({ date }) => {
        const slot = r.byDate[date];
        if (!slot) return;
        if (slot.opening != null) {
          opening[date] = (opening[date] ?? 0) + slot.opening;
          openCount[date] = (openCount[date] || 0) + 1;
          if (slot.openingIsReal) realCount[date] = (realCount[date] || 0) + 1;
        }
        if (slot.ending != null) ending[date] = (ending[date] ?? 0) + slot.ending;
      });

      if (r.latest != null) owed += r.latest;
      if (r.account.credit_limit != null) limit += Number(r.account.credit_limit);
      if (r.account.currency) currencies.add(r.account.currency);
    });

    const byDate = {};
    days.forEach(({ date }) => {
      byDate[date] = {
        opening: opening[date] ?? null,
        ending: ending[date] ?? null,
        openingIsReal: openCount[date] > 0 && realCount[date] === openCount[date],
      };
    });

    return {
      account: {
        id: CARD_AGGREGATE_ID,
        label: `All Credit Cards (${cards.length})`,
        account_type: "credit_card",
        // Only badge a real currency when every card agrees. Otherwise "MIXED",
        // which falls through CurrencyBadge to its neutral chip: the amounts are
        // summed RAW because this page has no FX source, so a "CAD" chip over a
        // total containing USD would be a false claim, and a blank one reads as
        // missing data rather than as a caveat.
        currency: currencies.size === 1 ? [...currencies][0] : "MIXED",
        credit_limit: limit,
      },
      byDate,
      flows,
      details,
      childrenBySection: {
        inflows: sortOutflowRows([...childKeys.inflows].map(([key, label]) => ({ key, label }))),
        outflows: sortOutflowRows([...childKeys.outflows].map(([key, label]) => ({ key, label }))),
      },
      otherChildren: [...otherKeys]
        .map(([key, label]) => ({ key, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      latest: owed,
      isLiability: true,
      isCard: true,
      entityName: "All Credit Cards",
      cardCount: cards.length,
    };
  }, [groupMode, rows, days]);

  // Group by entity so multi-select stays readable. In card mode there is one
  // group holding one row, and entity headers are suppressed — splitting the
  // combined row back out by entity would undo the combining.
  const groups = useMemo(() => {
    if (groupMode === "card") {
      return cardAggregate ? [{ name: "All Credit Cards", rows: [cardAggregate] }] : [];
    }
    const byEntity = new Map();
    rows.forEach((r) => {
      if (!byEntity.has(r.entityName)) byEntity.set(r.entityName, []);
      byEntity.get(r.entityName).push(r);
    });
    return Array.from(byEntity.entries())
      .map(([name, groupRows]) => ({ name, rows: groupRows }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [groupMode, cardAggregate, rows]);

  // Card mode has exactly one group, so this is already false there.
  const multiEntity = groups.length > 1;

  // ── collapse state ─────────────────────────────────────────────────────────

  // Account collapse is CONTROLLED by the page, because the Expand/Collapse All
  // button sits in the page toolbar alongside the span/view toggles. Per-account
  // chevrons here and that button have to drive one set, or the two disagree.
  function toggleAccount(id) {
    const next = new Set(collapsedAccounts);
    next.has(id) ? next.delete(id) : next.add(id);
    onCollapsedAccountsChange?.(next);
  }

  // Section breakdowns are open by default, matching ClinicCashFlowCard's
  // sections. Holds the exceptions only, so an account gaining its first
  // payment shows the breakdown rather than hiding it. Keyed per account AND
  // per section — Inflows and Outflows collapse independently.
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const sectionId = (accountId, sectionKey) => `${accountId}:${sectionKey}`;

  function toggleSection(accountId, sectionKey) {
    const id = sectionId(accountId, sectionKey);
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // "Other payments" sub-rows start COLLAPSED — the opposite default to the
  // sections — because a free-text expense list can run long. Holds the
  // accounts that have it open.
  const [expandedOther, setExpandedOther] = useState(() => new Set());

  function toggleOther(accountId) {
    setExpandedOther((prev) => {
      const next = new Set(prev);
      next.has(accountId) ? next.delete(accountId) : next.add(accountId);
      return next;
    });
  }

  // ── moving a payment to another date ───────────────────────────────────────
  // Two different mechanisms behind one modal, because the two sources model a
  // moved payment differently:
  //   schedule  → a one-off SchedulePaymentException; the recurring rule is
  //               untouched, so only this occurrence shifts.
  //   statement → no exception model. The date is edited directly in the
  //               statement's `splits` array, which is the record itself.
  const [moveCell, setMoveCell] = useState(null);
  const createException = useCreateSchedulePaymentException();
  const updateException = useUpdateSchedulePaymentException();
  const bulkExceptions = useBulkSchedulePaymentExceptions();
  const updateStatement = useUpdateStatementPayment();
  const savingMove =
    createException.isPending ||
    updateException.isPending ||
    bulkExceptions.isPending ||
    updateStatement.isPending;

  // ── editing the SERIES behind an occurrence ────────────────────────────────
  // Mirrors the clinic grid: the move modal edits one occurrence, this opens
  // the schedule itself so a wrong recurring amount or interval can be fixed
  // without leaving for the All Payments tab. `groupBy="entity"` here — these
  // accounts have no clinic to name.
  const [editingSchedule, setEditingSchedule] = useState(null);
  const updatePayment = useUpdateSchedulePayment();
  const deletePayment = useDeleteSchedulePayment();
  const canDeleteSchedule = canMove && canDeleteSchedulePayments(user);

  const scheduleAccountOptions = useMemo(
    () =>
      accounts.map((a) => ({
        value: a.id,
        label: a.label,
        clinicCode: a.clinic_code ?? null,
        entityName: a.entity_name ?? null,
        currency: a.currency,
      })),
    [accounts],
  );

  function openScheduleEditor(occurrence) {
    const rows = schedulesQuery.data?.results ?? schedulesQuery.data ?? [];
    const schedule = rows.find((sp) => sp.id === occurrence?.scheduleId);
    if (!schedule) {
      showApiError(new Error("That scheduled payment could not be loaded."));
      return;
    }
    // One at a time — see the clinic grid: two open editors on one series can
    // save two conflicting versions of it.
    setMoveCell(null);
    setEditingSchedule(schedule);
  }

  const scheduleInitialValues = useMemo(() => {
    if (!editingSchedule) return null;
    return {
      bank_account: editingSchedule.bank_account ?? "",
      expense_type: editingSchedule.expense_type,
      payment_type: editingSchedule.payment_type,
      payment_interval: editingSchedule.payment_interval,
      payment_weekdays: editingSchedule.payment_weekdays ?? [],
      amount: editingSchedule.amount ?? "",
      start_date: editingSchedule.start_date ?? "",
      end_date: editingSchedule.end_date ?? "",
      notes: editingSchedule.notes ?? "",
      status: editingSchedule.status,
    };
  }, [editingSchedule]);

  async function handleScheduleSave(form) {
    if (!editingSchedule) return;
    try {
      await updatePayment.mutateAsync({
        id: editingSchedule.id,
        bank_account: form.bank_account || undefined,
        expense_type: form.expense_type,
        payment_type: form.payment_type,
        payment_interval: form.payment_interval,
        payment_weekdays: form.payment_weekdays ?? [],
        amount: form.amount,
        start_date: form.start_date,
        end_date: form.end_date || null,
        notes: form.notes,
        status: form.status,
      });
      showSuccess("Scheduled payment updated.");
      setEditingSchedule(null);
    } catch (err) {
      showApiError(err);
    }
  }

  async function handleScheduleDelete() {
    if (!editingSchedule) return;
    try {
      await deletePayment.mutateAsync(editingSchedule.id);
      showSuccess("Scheduled payment deleted.");
      setEditingSchedule(null);
    } catch (err) {
      showApiError(err);
    }
  }

  async function handleMove(changes) {
    if (!changes.length) {
      setMoveCell(null);
      return;
    }
    try {
      const statements = statementsQuery.data?.results ?? statementsQuery.data ?? [];

      // Statement edits are grouped per statement FIRST: two payments of the
      // same statement moved together must go out as ONE patch, or the second
      // would be built from a stale `splits` array and undo the first.
      const splitsByStatement = new Map();
      const scheduleJobs = [];
      // Skips go out through the BULK endpoint rather than the create/update
      // pair the moves use: identity for a skip is (schedule, original_date) —
      // the only thing the grid reliably knows — and bulk upserts on exactly
      // that, so it lands whether or not an exception already exists and whether
      // or not this page's `exceptionId` is current. A statement payment never
      // reaches here: it has no exception model, so the modal withholds the
      // button entirely.
      const skips = [];

      changes.forEach(({ occurrence, newDate, newAmount, skip, reason }) => {
        if (skip) {
          skips.push({
            schedule: occurrence.scheduleId,
            // NEVER the displayed date: an overdue occurrence is drawn on the
            // roll target, and skipping that would key the exception to an
            // occurrence the rule never produced.
            original_date: occurrence.originalDate,
            new_date: null,
            // Omitted when blank so an upsert onto an existing exception does
            // not wipe a note somebody already wrote there.
            ...(reason ? { notes: reason } : {}),
          });
          return;
        }
        if (occurrence.kind === "statement") {
          // Date only. A statement's splits must add up to its balance, so a
          // single split's amount cannot change on its own — the modal keeps
          // that field read-only for statement rows.
          if (!newDate) return;
          const current =
            splitsByStatement.get(occurrence.statementId) ??
            (statements.find((s) => s.id === occurrence.statementId)?.splits ?? []).map((s) => ({
              ...s,
            }));
          if (current[occurrence.splitIndex]) current[occurrence.splitIndex].date = newDate;
          splitsByStatement.set(occurrence.statementId, current);
          return;
        }
        // `amount` is sent ONLY when the user edited it, so a date-only move
        // leaves an existing override (or the schedule's own amount) alone. A
        // null new_date means SKIP, so an amount-only edit re-pins the date the
        // occurrence already sits on.
        const amountPatch = newAmount != null ? { amount: newAmount } : {};
        scheduleJobs.push(
          occurrence.exceptionId
            ? updateException.mutateAsync({
                id: occurrence.exceptionId,
                new_date: newDate ?? occurrence.currentDate,
                ...amountPatch,
              })
            : createException.mutateAsync({
                schedule: occurrence.scheduleId,
                original_date: occurrence.originalDate,
                new_date: newDate ?? occurrence.currentDate,
                ...amountPatch,
              }),
        );
      });

      await Promise.all([
        ...scheduleJobs,
        ...(skips.length ? [bulkExceptions.mutateAsync(skips)] : []),
        ...Array.from(splitsByStatement.entries()).map(([id, splits]) =>
          updateStatement.mutateAsync({ id, splits }),
        ),
      ]);
      showSuccess(
        skips.length && !scheduleJobs.length && !splitsByStatement.size
          ? "Payment skipped. Restore it from All Payments if that was a mistake."
          : "Payment updated.",
      );
      setMoveCell(null);
    } catch (err) {
      showApiError(err);
    }
  }

  const columns = useMemo(() => {
    // A column carries THREE date fields, and cellFor reads a different one per
    // row type: `days` (every day it covers — flows sum over these),
    // `openingDate` (its first day — Opening Balance), `date` (its last day —
    // Ending Balance). For a daily column all three collapse to the same day;
    // for a week they genuinely differ.
    //
    // Every column renders in full. Past days used to fold into one "Past"
    // column, which mattered when the window was a calendar month starting on
    // the 1st; now the window starts at today, so there is usually nothing
    // behind it to fold.
    const cols =
      viewMode === "weekly"
        ? groupDaysIntoWeeks(days).map((w) => ({
            key: w.key,
            openingDate: w.days[0].date,
            date: w.days[w.days.length - 1].date,
            sublabel: w.header1,
            label: w.header2,
            isExpected: w.isExpected,
            days: w.days,
          }))
        : days.map((d) => ({
            key: `d-${d.date}`,
            date: d.date,
            label: fmtDayHeader(d.date),
            isExpected: d.isExpected,
            days: [d],
          }));
    // Past columns render on a gray wash so settled history is never read as a
    // projection — same treatment as the clinic cards. `isExpected` is already
    // "date >= today", so past is its negation, and a WEEK is past only when
    // none of its days remain. `isPastEdge` is the last past column, which
    // carries the divider at today.
    return cols.map((c, i) => ({
      ...c,
      isPast: !c.isExpected,
      isPastEdge: !c.isExpected && !!cols[i + 1]?.isExpected,
    }));
  }, [days, viewMode]);

  // Totals describe WHAT IS ON SCREEN. In card mode the grid shows only cards, so
  // including bank accounts here would report cash the table does not contain.
  const totals = useMemo(() => {
    const summaryRows =
      groupMode === "card" ? rows.filter((r) => r.account.account_type === "credit_card") : rows;
    let held = 0;
    let owed = 0;
    let limit = 0;
    // A credit LIMIT is a property of the card, so it counts whether or not
    // anyone has entered a balance yet. Only the BALANCE is skipped when missing.
    //
    // These used to share one `if (r.latest == null) return`, which skipped the
    // limit too: a newly-added card showed in the grid (with "—" balances, since
    // there is nothing to project from) while Credit Limit and Credit Available
    // silently dropped its whole limit. That also read the same missing balance
    // two ways in adjacent tiles — Owed treated it as zero owed, Limit treated the
    // card as non-existent.
    summaryRows.forEach((r) => {
      if (r.isLiability) {
        limit += r.account.credit_limit != null ? Number(r.account.credit_limit) : 0;
        if (r.latest != null) owed += r.latest;
      } else if (r.latest != null) {
        held += r.latest;
      }
    });
    return { held, owed, limit, available: limit - owed };
  }, [rows, groupMode]);

  // The schedules query stays disabled until the accounts resolve, and a
  // disabled query does not report isLoading — so it can't gate the first paint
  // on its own. The accounts query covers that window.
  const isLoading =
    accountsQuery.isLoading ||
    balancesQuery.isLoading ||
    schedulesQuery.isLoading ||
    statementsQuery.isLoading;

  if (isLoading) return <Loading text="Loading accounts..." />;

  if (groupMode === "card" && !cardAggregate) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-dashed border-palette-gray-divider bg-white py-20 text-sm text-palette-gray-placeholder">
        No active credit cards for the selected{" "}
        {entityIds.length === 1 ? "entity" : `${entityIds.length} entities`}.
      </div>
    );
  }

  if (accounts.length === 0) {
    // Distinguish "this entity owns no accounts" from "the account picker
    // filtered them all out" — otherwise the second reads as missing data.
    const unfiltered = accountsQuery.data?.results ?? accountsQuery.data ?? [];
    return (
      <div className="flex items-center justify-center rounded-2xl border border-dashed border-palette-gray-divider bg-white py-20 text-sm text-palette-gray-placeholder">
        {unfiltered.length > 0 ? (
          "No accounts match the current Accounts filter."
        ) : (
          <>
            No active bank accounts for the selected{" "}
            {entityIds.length === 1 ? "entity" : `${entityIds.length} entities`}.
          </>
        )}
      </div>
    );
  }

  // Sum a flow series over every day a column covers. A collapsed "Past" column
  // spans many days, and unlike a balance a flow IS additive over them.
  function sumFlow(series, col) {
    if (!series) return 0;
    return col.days.reduce((total, d) => total + (series[d.date] ?? 0), 0);
  }

  // What a hovered cell lists. Gathered across EVERY day the column covers, so a
  // weekly or collapsed-Past column explains its own total rather than going
  // blank — the clinic cards only show a breakdown on single-day columns, which
  // is exactly where a wide column leaves you guessing.
  // Payments in a cell that can be relocated. Single-day columns only — "move
  // this payment" has no answer on a weekly or aggregated column. Returned
  // UNMERGED, unlike the hover breakdown: each one is moved individually, so two
  // payments from the same source must stay distinct here.
  function movablesFor(row, rowDef, col) {
    if (col.days.length !== 1) return [];
    const items = row.details?.[rowDef.key]?.[col.days[0].date] ?? [];
    return items
      .filter((it) => it.move)
      .map((it) => ({ ...it.move, label: it.label, amount: it.amount }));
  }

  // One line per (day, source): two statements paid by the same bank on the same
  // day are one line for that bank, not the same name twice. The cell itself
  // always shows the combined total; this is only how that total is itemised.
  function detailsFor(row, rowDef, col) {
    const byDate = row.details?.[rowDef.key];
    if (!byDate) return [];
    const merged = new Map();
    col.days.forEach((d) => {
      (byDate[d.date] ?? []).forEach((it) => {
        const key = `${d.date}|${it.label}`;
        const prev = merged.get(key);
        if (prev) prev.amount += Number(it.amount) || 0;
        else merged.set(key, { date: d.date, label: it.label, amount: Number(it.amount) || 0 });
      });
    });
    return Array.from(merged.values()).sort((a, b) =>
      a.date === b.date ? b.amount - a.amount : a.date < b.date ? -1 : 1,
    );
  }

  // A cell's value for one line item in one column.
  function cellFor(rowDef, row, col) {
    if (rowDef.pending) return { value: null, real: false, pending: true };

    // ── flows ───────────────────────────────────────────────────────────────
    // Sections and children read the same way; `dir` decides sign and colour.
    if (rowDef.type === "section" || rowDef.type === "child") {
      return {
        value: sumFlow(row.flows[rowDef.key], col),
        real: true,
        pending: false,
        flow: rowDef.dir,
      };
    }
    if (rowDef.type === "net") {
      const net = sumFlow(row.flows.inflows, col) - sumFlow(row.flows.outflows, col);
      return { value: net, real: true, pending: false, flow: net < 0 ? "out" : "in" };
    }

    // ── balances (positions, never summed) ──────────────────────────────────
    const { byDate } = row;
    const openingSlot = byDate[col.openingDate ?? col.date];
    const slot = byDate[col.date];

    // Reference row: the amount stays in `value` so it formats like any other
    // balance, with the capture time carried alongside for the second line. A
    // multi-day column reports the LAST day that has a figure — closing balances
    // are positions and cannot be summed, and falling back through the column
    // stops a blank final day from hiding an earlier entry.
    if (rowDef.type === "reference") {
      const dates = col.days?.length ? col.days.map((d) => d.date) : [col.date];
      for (let i = dates.length - 1; i >= 0; i -= 1) {
        const hit = byDate[dates[i]]?.closing;
        if (hit) return { value: hit.amount, real: true, pending: false, time: hit.time };
      }
      return { value: null, real: true, pending: false };
    }
    if (rowDef.key === "opening") {
      // Only the OPENING carries the estimate marker, matching the clinic cards:
      // it is real on any day with an entered balance and an estimate otherwise.
      return {
        value: openingSlot?.opening ?? null,
        real: !!openingSlot?.openingIsReal,
        pending: false,
      };
    }
    // Ending is always derived (opening + net), so it renders plainly rather
    // than as an estimate — again matching the clinic cards, which never mark it.
    return { value: slot?.ending ?? null, real: true, pending: false };
  }

  return (
    <>
      {/* Cash held and credit owed are deliberately separate — summing them would
          misstate the position, and there is no FX source to convert with either,
          so mixed-currency accounts are added raw within each group. */}
      <div className="flex flex-wrap gap-3 mb-6">
        {groupMode !== "card" && (
          <SummaryCard label="Cash on Hand (latest)" value={formatMoney(totals.held)} />
        )}
        <SummaryCard
          label="Credit Owed (latest)"
          value={formatMoney(totals.owed)}
          valueClass="text-smiles-redOrange"
        />
        <SummaryCard label="Credit Limit" value={formatMoney(totals.limit)} />
        <SummaryCard
          label="Credit Available"
          value={formatMoney(totals.available)}
          valueClass="text-smiles-green"
        />
      </div>

      <div className="rounded-2xl border border-palette-gray-divider bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="cf-table w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-palette-gray-divider bg-palette-gray-subtle">
                <th className="sticky left-0 z-20 bg-palette-gray-subtle px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-palette-gray-secondary min-w-[260px]">
                  Account / Line Item
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-3 py-2 text-right text-xs font-semibold text-palette-gray-secondary whitespace-nowrap min-w-[96px] ${
                      col.isPast ? "bg-palette-gray-border" : ""
                    } ${col.isPastEdge ? "border-r-2 border-palette-gray-secondary" : ""}`}
                    title={col.isPast ? "Past — already settled" : undefined}
                  >
                    {col.sublabel ? (
                      // Weekly: "WK OF" above the Monday that anchors the week.
                      <div className="flex flex-col items-end">
                        <span className="text-[10px] uppercase tracking-wide text-palette-gray-placeholder">
                          {col.sublabel}
                        </span>
                        <span className="text-palette-gray-heading">{col.label}</span>
                      </div>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            {groups.map((group) => (
              <tbody key={group.name}>
                {/* Entity header, only when more than one entity is selected —
                    with a single entity it would just repeat the filter. */}
                {multiEntity && (
                  <tr className="border-b border-palette-gray-divider bg-palette-indigo-soft/50">
                    <td
                      colSpan={1 + columns.length}
                      className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-palette-indigo-badge"
                    >
                      {group.name}
                      <span className="ml-2 font-medium normal-case tracking-normal text-palette-gray-secondary">
                        {group.rows.length} account{group.rows.length === 1 ? "" : "s"}
                      </span>
                    </td>
                  </tr>
                )}

                {group.rows.map((row) => {
                  const {
                    account,
                    byDate,
                    isLiability,
                    isCard,
                    childrenBySection,
                    otherChildren = [],
                    hasClosing,
                  } = row;
                  const otherOpen = expandedOther.has(account.id);
                  const open = !collapsedAccounts.has(account.id);
                  const kind = accountKind(account);
                  const isSectionOpen = (key) => !collapsedSections.has(sectionId(account.id, key));

                  // Expand each section into the sources this account actually
                  // has — expense types and card payments under Outflows, paying
                  // banks under Inflows. Nothing is hardcoded, so an account with
                  // no payments shows the section and no children.
                  const lineItems = ROW_DEFS.flatMap((def) => {
                    // Nothing entered for this account in view — no point in a
                    // row of dashes.
                    if (def.type === "reference" && !hasClosing) return [];
                    // Balance rows are relabelled per scale, or the figure reads
                    // as the wrong thing entirely:
                    //   card -> "Credit Balance", the amount owed;
                    //   LOC  -> "Available", the headroom left;
                    //   bank -> unchanged, a cash position.
                    const item =
                      def.type === "balance" && isCard
                        ? { ...def, label: def.label.replace("Balance", "Credit Balance") }
                        : def.type === "balance" && isLiability
                          ? { ...def, label: def.label.replace("Balance", "Available") }
                          : def;
                    if (def.type !== "section" || !isSectionOpen(def.key)) return [item];
                    const children = childrenBySection[def.key] ?? [];
                    return [
                      item,
                      ...children.flatMap((c) => {
                        const child = { ...c, type: "child", dir: def.dir };
                        if (c.key !== OTHER_ROW_KEY || otherChildren.length === 0) return [child];
                        const toggle = { ...child, expandable: true };
                        if (!otherOpen) return [toggle];
                        return [
                          toggle,
                          ...otherChildren.map((g) => ({
                            ...g,
                            type: "child",
                            dir: def.dir,
                            nested: true,
                          })),
                        ];
                      }),
                    ];
                  });
                  return (
                    // Keyed Fragment: an account contributes a header row plus its
                    // line-item rows, and a bare <> in a map would drop the key.
                    <Fragment key={account.id}>
                      {/* Account header — click to collapse its line items. */}
                      <tr className={`border-b border-palette-gray-divider ${kind.row}`}>
                        <td className={`sticky left-0 z-10 px-4 py-2.5 ${kind.row} ${kind.edge}`}>
                          <button
                            type="button"
                            onClick={() => toggleAccount(account.id)}
                            className="flex items-center gap-2 text-left hover:text-palette-indigo-badge transition-colors"
                          >
                            <Chevron open={open} className="w-3 h-3" />
                            <span className={`font-semibold truncate max-w-[220px] ${kind.text}`}>
                              {account.label}
                            </span>
                            <CurrencyBadge currency={account.currency} />
                          </button>
                        </td>
                        {columns.map((col) => {
                          const slot = byDate[col.date];
                          return (
                            <td
                              key={col.key}
                              // Red on a CARD: this cell is the credit balance,
                              // money owed. An LOC still shows headroom here and
                              // a bank shows cash, neither of which is a debt.
                              className={`px-3 py-2.5 text-right whitespace-nowrap tabular-nums text-xs font-semibold ${
                                isCard && slot?.ending
                                  ? "text-smiles-redOrange"
                                  : "text-palette-gray-heading"
                              } ${col.isPast ? "bg-palette-gray-divider" : ""} ${
                                col.isPastEdge ? "border-r-2 border-palette-gray-secondary" : ""
                              }`}
                              title={
                                isCard
                                  ? "Ending credit balance (amount owed)"
                                  : isLiability
                                    ? "Available credit (limit − owed)"
                                    : "Ending balance"
                              }
                            >
                              {fmtOrDash(slot?.ending)}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Line items */}
                      {open &&
                        lineItems.map((def) => {
                          const isSection = def.type === "section";
                          const isChild = def.type === "child";
                          const isNet = def.type === "net";
                          const isBalanceRow = def.type === "balance";
                          const isReference = def.type === "reference";
                          const sectionChildren = isSection
                            ? (childrenBySection[def.key] ?? [])
                            : [];
                          const sectionOpen = isSection && isSectionOpen(def.key);
                          const rowBg = isBalanceRow
                            ? "bg-palette-gray-subtle"
                            : isNet
                              ? "bg-palette-indigo-soft"
                              : "bg-white";
                          const dirColor =
                            def.dir === "in" ? "text-smiles-green" : "text-smiles-redOrange";
                          return (
                            <tr
                              key={`${account.id}-${def.key}`}
                              className={`border-b border-palette-gray-divider last:border-0 ${rowBg}`}
                            >
                              <td
                                className={`sticky left-0 z-10 px-4 ${
                                  isReference ? "py-1" : "py-2"
                                } ${rowBg} ${def.nested ? "pl-24" : isChild ? "pl-16" : "pl-11"} ${
                                  isSection
                                    ? `font-bold ${dirColor}`
                                    : isNet
                                      ? "font-bold text-palette-gray-primary"
                                      : isBalanceRow
                                        ? "font-semibold text-palette-gray-primary"
                                        : isReference
                                          ? // Deliberately the faintest row in
                                            // the table: it must not read as
                                            // another projection line.
                                            "text-[11px] font-normal text-palette-gray-placeholder"
                                          : "text-palette-gray-heading"
                                }`}
                              >
                                {isSection ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleSection(account.id, def.key)}
                                    disabled={sectionChildren.length === 0}
                                    className={`flex items-center gap-2 text-left transition-colors hover:text-palette-indigo-badge disabled:cursor-default disabled:hover:${
                                      def.dir === "in"
                                        ? "text-smiles-green"
                                        : "text-smiles-redOrange"
                                    }`}
                                    title={
                                      sectionChildren.length === 0
                                        ? def.dir === "in"
                                          ? "Nothing paid into this account"
                                          : "No payments out of this account"
                                        : sectionOpen
                                          ? "Hide breakdown"
                                          : "Show breakdown"
                                    }
                                  >
                                    {sectionChildren.length > 0 && (
                                      <Chevron open={sectionOpen} className="w-3 h-3" />
                                    )}
                                    {def.label}
                                  </button>
                                ) : def.expandable ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleOther(account.id)}
                                    className="flex items-center gap-2 text-left transition-colors hover:text-palette-indigo-badge"
                                    title={otherOpen ? "Hide expense types" : "Show expense types"}
                                  >
                                    <Chevron open={otherOpen} className="w-3 h-3" />
                                    {def.label}
                                  </button>
                                ) : (
                                  <>
                                    {def.label}
                                    {def.sublabel && (
                                      <span className="ml-2 text-[10px] text-palette-gray-placeholder">
                                        {def.sublabel}
                                      </span>
                                    )}
                                    {def.pending && (
                                      <span
                                        className="ml-2 text-[10px] uppercase tracking-wide text-palette-gray-placeholder"
                                        title="No data source yet — see ROW_DEFS"
                                      >
                                        no source yet
                                      </span>
                                    )}
                                  </>
                                )}
                              </td>
                              {columns.map((col) => {
                                const cell = cellFor(def, row, col);
                                // A zero flow is noise on a 60-day grid — render
                                // it as a dash so the days that DO have a payment
                                // stand out.
                                const emptyFlow = cell.flow && !cell.value;
                                // `flow` is "in" or "out", not a boolean: an
                                // inflow and an outflow read the same magnitude
                                // and must not look alike.
                                const isOut = cell.flow === "out";
                                const flowColor = isOut
                                  ? "text-smiles-redOrange"
                                  : "text-smiles-green";
                                const flowBg =
                                  emptyFlow || !cell.flow || isNet
                                    ? ""
                                    : isOut
                                      ? "bg-palette-rose-soft"
                                      : "bg-palette-emerald-soft";
                                // Past days sit on a gray wash, as BASE only: a
                                // cell that earned an in/out colour keeps it —
                                // which way the money went still matters on a
                                // settled day. The gray header band and the
                                // divider at today mark the boundary regardless.
                                const cellBg =
                                  flowBg || (col.isPast ? "bg-palette-gray-divider" : "");
                                // What made up this cell. Only on flow cells with
                                // a value — a balance is a position, not a sum of
                                // things, so it has nothing to itemise.
                                const items =
                                  cell.flow && !emptyFlow ? detailsFor(row, def, col) : [];
                                // Only CHILD rows are movable: a section total
                                // mixes sources, and "move this total" has no
                                // single meaning.
                                const movables =
                                  canMove && isChild && !emptyFlow
                                    ? movablesFor(row, def, col)
                                    : [];
                                const clickable = movables.length > 0;
                                return (
                                  <td
                                    key={col.key}
                                    onClick={
                                      clickable
                                        ? () =>
                                            setMoveCell({
                                              code: account.label,
                                              occurrences: movables,
                                            })
                                        : undefined
                                    }
                                    className={`px-3 text-right whitespace-nowrap tabular-nums ${
                                      isReference ? "py-1 text-[11px]" : "py-2"
                                    } ${cellBg} ${
                                      col.isPastEdge
                                        ? "border-r-2 border-palette-gray-secondary"
                                        : ""
                                    } ${items.length ? "group relative" : ""} ${
                                      clickable
                                        ? "cursor-pointer underline decoration-dotted underline-offset-2"
                                        : ""
                                    } ${
                                      cell.value == null || emptyFlow
                                        ? "text-palette-gray-placeholder"
                                        : cell.flow
                                          ? `${flowColor} ${isNet || isSection ? "font-semibold" : ""}`
                                          : isReference
                                            ? // Reference data: never the solid
                                              // near-black of an entered balance.
                                              "font-normal text-palette-gray-placeholder"
                                            : cell.real
                                              ? // A card's balance rows are money
                                                // owed, so they read red like the
                                                // Credit Owed tile. Estimates keep
                                                // the italic indigo either way —
                                                // real-vs-projected has to stay
                                                // legible on top of the scale.
                                                isCard
                                                ? "text-smiles-redOrange font-medium"
                                                : "text-palette-gray-primary font-medium"
                                              : "italic text-palette-indigo-badge"
                                    }`}
                                    // The hover panel replaces the native tooltip
                                    // where there is one — two tooltips on the
                                    // same cell fight each other.
                                    title={
                                      items.length
                                        ? undefined
                                        : cell.flow === "in"
                                          ? "Payment received (card balance paid down)"
                                          : cell.flow === "out"
                                            ? "Payment out"
                                            : isReference
                                              ? "Closing balance as entered — reference only, not part of the projection"
                                              : isCard
                                                ? `Credit balance owed${
                                                    cell.real ? " as entered" : " — carried forward"
                                                  }`
                                                : isLiability
                                                  ? `Available credit (limit − owed)${
                                                      cell.real ? "" : " — carried forward"
                                                    }`
                                                  : cell.real
                                                    ? "Entered balance"
                                                    : "Carried forward (estimate)"
                                    }
                                  >
                                    {cell.value == null || emptyFlow
                                      ? "—"
                                      : cell.flow
                                        ? `${isOut ? "-" : "+"}${fmtOrDash(Math.abs(cell.value))}`
                                        : `${cell.real ? "" : "~"}${fmtOrDash(cell.value)}`}

                                    {/* Capture time, under the closing figure. */}
                                    {cell.time && (
                                      <div className="text-[10px] font-normal leading-tight text-palette-gray-border">
                                        {fmtClockTime(cell.time)}
                                      </div>
                                    )}

                                    {items.length > 0 && (
                                      <div className="pointer-events-none absolute bottom-full right-2 z-30 mb-1 hidden w-64 rounded-lg border border-palette-gray-divider bg-white p-2 text-left shadow-lg group-hover:block">
                                        <div className="mb-1 border-b border-palette-gray-divider pb-1 text-[10px] font-semibold uppercase tracking-wider text-palette-gray-secondary">
                                          {def.label}
                                        </div>
                                        {items.map((item, idx) => (
                                          <div
                                            key={idx}
                                            className="flex items-center justify-between gap-2 py-0.5 text-xs"
                                          >
                                            <span className="min-w-0 truncate text-palette-gray-heading">
                                              {/* The date only earns its place on a
                                                  multi-day column; on a single day
                                                  it repeats the header. */}
                                              {col.days.length > 1 && (
                                                <span className="mr-1 text-palette-gray-placeholder">
                                                  {fmtDayHeader(item.date)}
                                                </span>
                                              )}
                                              {item.label}
                                            </span>
                                            <span
                                              className={`shrink-0 font-semibold tabular-nums ${flowColor}`}
                                            >
                                              {isOut ? "-" : "+"}
                                              {fmtOrDash(Math.abs(Number(item.amount)))}
                                            </span>
                                          </div>
                                        ))}
                                        {/* Only worth a total line when the cell is
                                            actually a sum of several things. */}
                                        {items.length > 1 && (
                                          <div className="mt-1 flex items-center justify-between gap-2 border-t border-palette-gray-divider pt-1 text-xs font-semibold">
                                            <span className="text-palette-gray-secondary">
                                              {items.length} payments
                                            </span>
                                            <span className={`tabular-nums ${flowColor}`}>
                                              {isOut ? "-" : "+"}
                                              {fmtOrDash(Math.abs(cell.value))}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                    </Fragment>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-palette-gray-secondary">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm border border-palette-gray-border" />
          Entered balance
        </span>
        <span className="flex items-center gap-1.5">
          <span className="italic text-palette-indigo-badge">~</span>
          Carried forward / derived
        </span>
        <span className="flex items-center gap-1.5">
          <span className="font-medium text-palette-gray-primary">$0.00</span>
          Credit card / LOC — rows show AVAILABLE credit (limit − owed)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="rounded-sm bg-palette-rose-soft px-1.5 py-0.5 font-medium text-smiles-redOrange">
            -$0.00
          </span>
          Payment out (scheduled or card statement)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="rounded-sm bg-palette-emerald-soft px-1.5 py-0.5 font-medium text-smiles-green">
            +$0.00
          </span>
          Card balance paid down
        </span>
        <span className="flex items-center gap-1.5">
          <span className="rounded-sm border border-palette-gray-divider px-1.5 py-0.5 text-palette-gray-secondary">
            hover
          </span>
          Hover a flow cell to see what made it up
        </span>
        {canMove && (
          <span className="flex items-center gap-1.5">
            <span className="underline decoration-dotted underline-offset-2">click</span>
            Click a payment cell to change its date or amount
          </span>
        )}
      </div>

      <ConfirmNonBankingMovesModal
        isOpen={nonBankingMoves.pending.length > 0}
        moves={nonBankingMoves.pending}
        onConfirm={nonBankingMoves.confirm}
        onClose={nonBankingMoves.dismiss}
        saving={nonBankingMoves.saving}
      />

      <SchedulePaymentModal
        isOpen={canMove && !!editingSchedule}
        onClose={() => setEditingSchedule(null)}
        onSave={handleScheduleSave}
        saving={updatePayment.isPending}
        accountOptions={scheduleAccountOptions}
        groupBy="entity"
        initialValues={scheduleInitialValues}
        isEdit
        onDelete={canDeleteSchedule ? handleScheduleDelete : undefined}
        deleting={deletePayment.isPending}
      />

      <MoveOccurrenceModal
        isOpen={canMove && !!moveCell}
        cell={moveCell}
        onClose={() => setMoveCell(null)}
        onSave={handleMove}
        saving={savingMove}
        onEditSchedule={canMove ? openScheduleEditor : undefined}
      />
    </>
  );
}
